import React, { Component } from 'react';
import { bool, string, object } from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import get from 'lodash.get';

import storage from 'dibs-browser-storage';

import Dropdown, { alignConstants } from '../Dropdown/Dropdown';
import DropdownBody from '../Dropdown/DropdownBody';
import DropdownLoadingContent from '../Dropdown/DropdownLoadingContent';
import NotificationsIcon from './NotificationsIcon';
import {
    setPromoCodeNotificationShown,
    wasPromoCodeNotificationShown,
} from '../../utils/promoCode.es';
import { getNotificationName, trackNotification } from './notificationTracking';

//imports for shareActivityAlert
import { trackEvent } from 'dibs-tracking';
import { shareActivitiesDisplay } from './trackingHelpers';
import ShareActivityAlert from './ShareActivityAlert';
import { shouldDisableNotifications } from '../../utils/notificationsHelper';

const BATCH_SIZE = 5;
const AUTOMATED_OFFER_VIEW_COUNT_DROPDOWN_TRIGGER_TRESHOLD = 2;
const LOCAL_STORAGE_KEY_NAME = 'NAV_NOTIFICATIONS_DROPDOWN_LAST_OPEN_TIME';
const SHARE_ACTIVITY_AUTO_COUNT_STORAGE_KEY = 'SHARE_ACTIVITY_AUTO_COUNT_STORAGE_KEY';
const DATE_OF_VIEWS_STORAGE_KEY = 'DATE_OF_VIEWS_STORAGE_KEY';
const AUTOMATED_OFFER_COUNT_STORAGE_KEY = 'AUTOMATED_OFFER_COUNT_STORAGE_KEY';

const resetState = () => ({
    isOpen: false,
    fetchingNotifications: true,
    first: BATCH_SIZE,
});
const stopFetching = (isMore = false) => {
    const defaultReturn = { fetchingNotifications: false };
    return isMore ? Object.assign({}, defaultReturn) : defaultReturn;
};
const startFetching = () => ({ fetchingNotifications: true });
const openDropdown = () => ({ isOpen: true });
const hasNewNotification = (lastUnreadMessageDate, lastUnreadNotificationDate) => {
    const { localStorage } = storage;

    const mostRecentMessageReceivedAt = lastUnreadMessageDate
        ? new Date(lastUnreadMessageDate).getTime()
        : 0;
    const mostRecentNotificationReceivedAt = lastUnreadNotificationDate
        ? new Date(lastUnreadNotificationDate).getTime()
        : 0;

    const mostRecent = Math.max(mostRecentMessageReceivedAt, mostRecentNotificationReceivedAt);
    const lastOpen = Number(localStorage.getItem(LOCAL_STORAGE_KEY_NAME)) || 0;
    return mostRecent > lastOpen;
};
const isNewNotification = eventDate =>
    new Date(eventDate).getTime() > Number(localStorage.getItem(LOCAL_STORAGE_KEY_NAME)) || 0;
const trackNotificationAlerts = notifications => {
    notifications.forEach(notification => {
        const { eventDate, type } = notification;
        if (type === 'TRADE_REWARD') {
            return;
        }

        if (isNewNotification(eventDate)) {
            trackNotification(getNotificationName(type), 'alert displayed');
        }
    });
};

function hasShowActivityAlertExpired() {
    let endDate = localStorage.getItem(DATE_OF_VIEWS_STORAGE_KEY);

    if (endDate) {
        endDate = new Date(parseInt(endDate, 10));
    } else {
        endDate = new Date();
        const sixWeeks = 42;
        endDate.setDate(endDate.getDate() + sixWeeks);
        localStorage.setItem(DATE_OF_VIEWS_STORAGE_KEY, endDate.getTime());
    }
    return endDate < new Date();
}

export class NotificationsComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            ...resetState(),
            AsyncComponents: null,
            showShareActivity: false,
            triggerDropdown: false,
        };

        this.loadNotifications = this.loadNotifications.bind(this);
        this.onMouseEnterIcon = this.onMouseEnterIcon.bind(this);
        this.loadComponents = this.loadComponents.bind(this);
        this.lastReadTime = 0;
        this.showShareActivityAlert = this.showShareActivityAlert.bind(this);
    }

    componentDidMount() {
        this.lastReadTime = Number(localStorage.getItem(LOCAL_STORAGE_KEY_NAME)) || 0;
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.user && this.props.user) {
            const feed = get(this.props, 'user.activityFeed.edges');
            if (Array.isArray(feed)) {
                trackNotificationAlerts(feed.map(edge => edge.node));
            }
            this.showShareActivityAlert();
            this.showAutomatedOfferAlert();
        }
    }

    showShareActivityAlert() {
        const props = this.props;
        const { viewer, user } = props;
        const isBuyerActivityDropdownDisabled = get(viewer, 'isBuyerActivityDropdownDisabled');
        const timesShown = localStorage.getItem(SHARE_ACTIVITY_AUTO_COUNT_STORAGE_KEY) || 0;
        const isExpired = hasShowActivityAlertExpired();
        const isUS = get(viewer, 'regInfo[0].countryCode') === 'US';
        const shareActivities = get(user, 'preferences.shareActivities') || false;

        if (
            !isUS &&
            !shareActivities &&
            !isExpired &&
            !isBuyerActivityDropdownDisabled &&
            !shouldDisableNotifications()
        ) {
            const triggerDropdown = timesShown <= 3;
            this.setState(
                {
                    showShareActivity: true,
                    triggerDropdown,
                },
                () => {
                    if (triggerDropdown) {
                        const trigger = 'auto-displayed';
                        trackEvent(shareActivitiesDisplay({ trigger, timesShown }));
                        localStorage.setItem(SHARE_ACTIVITY_AUTO_COUNT_STORAGE_KEY, timesShown + 1);
                        this.loadNotifications();
                    }
                }
            );
        }
    }

    showAutomatedOfferAlert() {
        const { viewer, user } = this.props;

        const isAutomatedOfferNotificationDisabled = get(
            viewer,
            'isAutomatedOfferNotificationDisabled'
        );

        if (shouldDisableNotifications() || isAutomatedOfferNotificationDisabled) {
            return;
        }

        const feed = get(user, 'activityFeed.edges');
        let savedAutomatedOfferViewCounts = {};

        try {
            savedAutomatedOfferViewCounts = JSON.parse(
                localStorage.getItem(AUTOMATED_OFFER_COUNT_STORAGE_KEY) || '{}'
            );
        } catch (e) {
            /* ignore */
        }

        const automatedOfferViewCounts = feed.reduce((viewCounts, currentNotification) => {
            if (currentNotification.node.type === 'AUTOMATED_OFFER') {
                const notificationId = currentNotification.node.serviceId;
                return {
                    ...viewCounts,
                    [notificationId]: (savedAutomatedOfferViewCounts[notificationId] || 0) + 1,
                };
            }
            return viewCounts;
        }, {});

        const smallestViewCount = Object.keys(automatedOfferViewCounts).reduce(
            (smallestCount, currentKey) => {
                if (automatedOfferViewCounts[currentKey] < smallestCount) {
                    return automatedOfferViewCounts[currentKey];
                } else {
                    return smallestCount;
                }
            },
            AUTOMATED_OFFER_VIEW_COUNT_DROPDOWN_TRIGGER_TRESHOLD + 1
        );

        const triggerDropdown =
            smallestViewCount <= AUTOMATED_OFFER_VIEW_COUNT_DROPDOWN_TRIGGER_TRESHOLD;
        this.setState({ triggerDropdown }, () => {
            if (triggerDropdown) {
                localStorage.setItem(
                    AUTOMATED_OFFER_COUNT_STORAGE_KEY,
                    JSON.stringify(automatedOfferViewCounts)
                );
            }
        });
    }

    loadNotifications() {
        const { localStorage } = storage;
        const { hasRootPendingRefetch, relay, userId, user } = this.props;
        const fetchNotificationVariables = {
            fetchNotifications: true,
            userId,
            hasUserId: !!userId,
        };

        if (hasRootPendingRefetch) {
            return;
        }

        const refetchVariables = fragmentVariables =>
            Object.assign({}, fragmentVariables, fetchNotificationVariables);
        const refetchCallback = () => {
            localStorage.setItem(LOCAL_STORAGE_KEY_NAME, Date.now());
            if (get(user, 'activePromotion')) {
                setPromoCodeNotificationShown(get(user, 'activePromotion'));
            }
            this.setState(stopFetching);
        };

        this.setState(startFetching, () =>
            relay.refetch(refetchVariables, null, refetchCallback, { force: true })
        );
    }

    onMouseEnterIcon(e) {
        e.stopPropagation();
        if (!this.state.isOpen) {
            this.loadNotifications();
            this.setState(openDropdown);
        }
    }

    loadComponents() {
        import(
            /* webpackChunkName: "NotificationsBodyComponents" */
            './NotificationsBodyComponents'
        ).then(AsyncComponents => {
            this.setState({ AsyncComponents });
        });
    }

    render() {
        const { hasUserId, isClient, user, viewer } = this.props;
        const {
            fetchingNotifications,
            AsyncComponents,
            showShareActivity,
            triggerDropdown,
        } = this.state;
        const conversationMetadata = get(this.props, 'initialViewer.conversations');
        const hasConversations = !!conversationMetadata;

        if (!isClient || (hasUserId && !hasConversations) || !hasUserId || !user) {
            return null;
        }

        const activePromotion = user.activePromotion;
        const conversations = get(this.props, 'viewer.conversations') || null;
        const feed = get(this.props, 'user.activityFeed') || null;
        const totalUnreadConversationCount = get(
            conversationMetadata,
            'metadata.totalUnreadConversationCount'
        );

        const lastUnreadMessageDate = get(conversationMetadata, 'metadata.lastUnreadMessageDate');
        const notifications = get(feed, 'edges') || [];
        const lastNotification = notifications.find(notification => {
            if (get(viewer, 'isAutomatedOfferNotificationDisabled')) {
                return get(notification, 'node.type') !== 'AUTOMATED_OFFER';
            } else {
                return true;
            }
        });
        const lastUnreadNotificationDate = get(lastNotification, 'node.eventDate');
        const hasNew = hasNewNotification(lastUnreadMessageDate, lastUnreadNotificationDate);

        return (
            <Dropdown
                hasDropdownHeader
                align={alignConstants.ALIGN_RIGHT}
                forceOpen={triggerDropdown}
                onShow={this.loadComponents}
                onHide={() => {
                    this.setState(resetState);
                }}
            >
                <NotificationsIcon
                    onMouseEnter={this.onMouseEnterIcon}
                    hasUnread={
                        hasNew ||
                        (activePromotion && !wasPromoCodeNotificationShown(activePromotion))
                    }
                />
                <DropdownBody>
                    <React.Fragment>
                        {showShareActivity && (
                            <ShareActivityAlert
                                user={user}
                                viewer={viewer}
                                autoDisplayed={triggerDropdown}
                            />
                        )}
                        {AsyncComponents ? (
                            <React.Fragment>
                                <AsyncComponents.ActivityFeed
                                    viewer={viewer}
                                    feed={feed}
                                    lastReadTime={this.lastReadTime}
                                    activePromotion={activePromotion}
                                />
                                <AsyncComponents.Conversations
                                    conversations={conversations}
                                    totalUnread={totalUnreadConversationCount}
                                    isLoading={fetchingNotifications}
                                />
                            </React.Fragment>
                        ) : (
                            <DropdownLoadingContent />
                        )}
                    </React.Fragment>
                </DropdownBody>
            </Dropdown>
        );
    }
}

NotificationsComponent.propTypes = {
    hasUserId: bool,
    hasRootPendingRefetch: bool,
    isClient: bool,
    relay: object,
    userId: string,
    initialViewer: object,
    viewer: object,
    user: object,
};

const Notifications = createRefetchContainer(
    NotificationsComponent,
    {
        initialViewer: graphql`
            fragment Notifications_initialViewer on Viewer {
                conversations(filter: "showUnreadOnly") @include(if: $hasUserId) {
                    metadata {
                        lastUnreadMessageDate
                        totalUnreadConversationCount
                    }
                }
            }
        `,
        viewer: graphql`
            fragment Notifications_viewer on Viewer
                @argumentDefinitions(
                    first: { type: "Int", defaultValue: 5 }
                    fetchNotifications: { type: "Boolean!", defaultValue: false }
                ) {
                regInfo: regionalInfo {
                    countryCode
                }
                isBuyerActivityDropdownDisabled: isEnabled(key: "isBuyerActivityDropdownDisabled")
                isAutomatedOfferNotificationDisabled: isEnabled(
                    key: "disableAutomatedOfferBellNotification"
                )
                conversations(filter: "showUnreadOnly", first: $first, notifications: true)
                    @include(if: $fetchNotifications) {
                    ...Conversations_conversations
                }
                ...ActivityFeed_viewer
            }
        `,
        user: graphql`
            fragment Notifications_user on User {
                activePromotion {
                    adjustmentRateList {
                        reference
                    }
                    serviceId
                    code
                    flatAdjustmentRate {
                        rate {
                            currency
                            amount
                        }
                    }
                    percentAdjustmentRate {
                        rate
                    }
                }
                activityFeed(
                    first: 3
                    elementTypes: [
                        PENDING_FEEDBACK
                        TRADE_REWARD
                        TRADE_REWARD_PRORATED
                        TRADE_REWARD_PROGRAM
                        AUTOMATED_OFFER
                    ]
                ) {
                    edges {
                        node {
                            type
                            eventDate
                            serviceId
                        }
                    }
                    ...ActivityFeed_feed
                }
                preferences {
                    shareActivities
                }
                ...ShareActivityAlert_user
            }
        `,
    },
    graphql`
        query NotificationsRefetchQuery($first: Int, $fetchNotifications: Boolean!) {
            viewer {
                ...Notifications_viewer
                    @arguments(first: $first, fetchNotifications: $fetchNotifications)
            }
        }
    `
);

export default Notifications;
